import { Component, EventEmitter, OnInit, OnDestroy, Output, ChangeDetectorRef, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { WwniMappingService } from './wwni-mapping.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-wwni-mapping',
    templateUrl: './wwni-mapping.component.html',
    styleUrls: ['./wwni-mapping.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class WwniMappingComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  // Standard component properties
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = false;

  // Modal properties
  modalTitle = "WWNI Allocation";

  // Tab management
  activeTab = 1;
  tabs = [
    { id: 1, name: "WWNI Allocation" },
    { id: 2, name: "Audit" }
  ];

  // Entity data properties removed as not present in original component

  // Data properties
  entities: any[] = [];
  mappingList: any[] = [];
  displayNdata: any[] = [];
  displayYdata: any[] = [];
  sysLeidDropdownNdata: any[] = [];
  varianceSubcategory: any[] = [];
  expla_code_dropdown: any[] = [];
  auditData: any[] = [];
  viewColumnNames: string[] = ["Updated On", "Compl. Entity", "DML Type", "Old-New Flag", "Trans Type", "Amount", "Comments", "Updated By"];

  // Calculation properties
  MAIN_SOURCE_AMT: number = 0;
  netIncPerLoss: number = 0;
  netIncRemain: number = 0;
  totalVariancea: number = 0;
  totalVarianceAllocateda: number = 0;
  totalVarianceRemaina: number = 0;

  // Flags
  changeColorwwni = false;
  changeColorVariance = false;
  tagetlistADD = false;
  noRecords = false;
  crudLoadingaudit = false;

  // Accordion states
  entityAccordionOpen = true;
  summaryAccordionOpen = true;

  // Loading state
  loading = true;

  // Sorting properties
  sort_key: any = {};
  column: string = '';
  reverse: boolean = false;

  // Global services
  private _rowData: any;
  private _filterParams: any;
  globalService: any;
  GENERAL_CONFIG: any;
  USER_SETTINGS: any;
  SERVER_CONFIG: any;

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private _snackBar: MatSnackBar,
    private wwniMappingService: WwniMappingService,
    private cdr: ChangeDetectorRef
  ) {
    // Initialize sort keys
    for (let i = 1; i < 9; i++) {
      this.sort_key[i] = 0;
    }
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => { this.applyWorkspaceContext(); this.init(); }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => { this.applyWorkspaceContext(); this.init(); }));
    this.init();
  }

  ngOnDestroy(): void { this.subs.unsubscribe(); }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.globalService = header.globalParams;
    this.GENERAL_CONFIG = header.generalConfigData;
    this.USER_SETTINGS = header.userSettingsData;
    this.SERVER_CONFIG = header.serverConfigData;
    this._filterParams = normalizeFilterParams(activeScreen);
    this._rowData = rowPayloadFromActiveScreen(activeScreen);
    this.cdr.detectChanges();
  }

  private resolveApiBase(): string {
    return this.GENERAL_CONFIG?.base_url || '';
  }

  init() {
    try {
      // Guard: prevent HTTP calls with undefined base_url
      if (!this.resolveApiBase()) {
        console.log('[WwniMapping] base_url not yet available, skipping init');
        return;
      }

      if (this._rowData && Object.keys(this._rowData).length > 0) {
        this.setupInitialData();
        this.loadWwniData();
        this.loadVarianceSubcategory();
      }
    } catch (e) {
      console.error('Error in WWNI Mapping init:', e);
    }
  }

  // setupEntityData method removed as entity table not present in original component

  private setupInitialData() {
    this.MAIN_SOURCE_AMT = this._rowData.TOTAL_SOURCE || 0;
    this.entities = [this._rowData]; // Initialize with current row data
    if (this.entities.length > 0) {
      this.entities[0].colorvalue = true;
    }
  }

  loadWwniData() {
    this.showFormLoader = true;
    this.crudLoading = true;

    const params = {
      action_code: '4bjt2k',
      tax_year: this.globalService?.tax_year,
      scenario: this.globalService?.scenario,
      jcd_key: this.globalService?.jcd_key,
      leid: this._rowData.SOURCE_LEID,
      cdr: this._rowData.SOURCE_CDR_NO,
      me: this._rowData.SOURCE_ME_CODE,
      cc_key: this._rowData.SOURCE_COMBINATION_KEY
    };

    this.wwniMappingService.loadWwniData(
      this.resolveApiBase(),
      params,
      this.globalService,
      this._filterParams,
      this._rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.showFormLoader = false;
        this.crudLoading = false;

        if (response.errorMessage && response.errorMessage !== 'null') {
          this._snackBar.open(response.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this.processWwniData(response.jsonObject || []);
        }
      },
      (error: any) => {
        this.showFormLoader = false;
        this.crudLoading = false;
        this._snackBar.open('Error loading WWNI data. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
      }
    );
  }

  processWwniData(data: any[]) {
    console.log('=== PROCESS WWNI DATA ===');
    console.log('Raw data from API:', data);
    console.log('Data length:', data.length);
    if (data.length > 0) {
      console.log('First item structure:', data[0]);
      console.log('First item DEFAULT_FLAG:', data[0].DEFAULT_FLAG);
      console.log('First item TARGET_KEY:', data[0].TARGET_KEY);
    }

    this.mappingList = [];
    this.displayNdata = [];
    this.displayYdata = [];
    this.sysLeidDropdownNdata = [];

    // Reset calculation values
    this.totalVariancea = 0;
    this.totalVarianceAllocateda = 0;
    this.totalVarianceRemaina = 0;
    this.netIncPerLoss = 0;
    this.netIncRemain = 0;
    this.changeColorwwni = false;
    this.changeColorVariance = false;

    // Separate data by DEFAULT_FLAG
    for (let item of data) {
      console.log('Processing item:', item.DEFAULT_FLAG, item.TARGET_KEY);
      if (item.DEFAULT_FLAG === "N") {
        this.displayNdata.push(item);
        this.sysLeidDropdownNdata.push(item.TARGET_KEY);
        console.log('Added to dropdown:', item.TARGET_KEY);
      } else {
        this.displayYdata.push(item);
      }
    }

    console.log('Final sysLeidDropdownNdata:', this.sysLeidDropdownNdata);
    console.log('displayNdata count:', this.displayNdata.length);
    console.log('displayYdata count:', this.displayYdata.length);

    // Process mapping list (main rows)
    for (let item of this.displayYdata) {
      if (item.ROW_INDICATOR === "M") {
        // Initialize variance data for each mapping item
        item.variancedata = [];
        item.colorvaluevariance = "gray";
        item.open = false; // Initialize expand/collapse state
        item.tagetlistADD = false; // Initialize target list flag
        item.saveError = false; // Initialize save error flag
        this.mappingList.push(item);
      }
    }

    // Process variance data for each mapping item
    for (let mapping of this.mappingList) {
      for (let item of data) {
        if (item.TARGET_COMBINATION_KEY === mapping.TARGET_COMBINATION_KEY && item.ROW_INDICATOR === "V") {
          const varianceItem = {
            TARGET_KEY: item.TARGET_COMBINATION_KEY,
            VARIANCE_ALLOCATED: item.VARIANCE_ALLOCATED || 0,
            VARIANCE_EXPL_CODE: item.VARIANCE_EXPL_CODE || '',
            VARIANCE_SUB_CATEFORY: item.VARIANCE_SUB_CATEFORY || '',
            ROW_KEY: item.ROW_KEY || 0,
            VARIANCE_COMMENTS: item.VARIANCE_COMMENTS || '',
            variance_Dropdown_Flag: false,
            varianceError: false,
            subcateDropdown: []
          };
          mapping.variancedata.push(varianceItem);
        }
      }

      // Ensure each mapping has at least one variance row
      if (mapping.variancedata.length === 0) {
        mapping.variancedata.push({
          VARIANCE_ALLOCATED: 0,
          VARIANCE_EXPL_CODE: '',
          VARIANCE_SUB_CATEFORY: '',
          VARIANCE_EXPL_CODE_KEY: '',
          ROW_KEY: '',
          VARIANCE_COMMENTS: '',
          variance_Dropdown_Flag: false,
          varianceError: false,
          subcateDropdown: []
        });
      }

      // Auto-expand if variance data has meaningful content
      mapping.open = this.hasVarianceData(mapping.variancedata);
    }

    // Ensure there's at least one mapping row
    if (this.mappingList.length === 0) {
      this.addRepointVariance();
    }

    // Remove duplicates from dropdown data
    this.sysLeidDropdownNdata = [...new Set(this.sysLeidDropdownNdata)];

    this.calculateTotals();
    this.updateVarianceColors();

    // Set loading to true to show the table
    this.loading = true;
  }

  loadVarianceSubcategory() {
    const params = {
      action_code: 'z5j10g',
      tax_year: this.globalService?.tax_year
    };

    this.wwniMappingService.loadVarianceSubcategory(
      this.resolveApiBase(),
      params,
      this.globalService,
      this._filterParams,
      this._rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response.errorMessage && response.errorMessage !== 'null') {
          this._snackBar.open(response.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this.varianceSubcategory = response.jsonObject || [];

          // Build explanation code dropdown
          this.expla_code_dropdown = [];
          for (let item of this.varianceSubcategory) {
            this.expla_code_dropdown.push(item.VARIANCE_EXPL_CODE);
          }
          this.expla_code_dropdown = [...new Set(this.expla_code_dropdown)];
        }
      },
      (error: any) => {
        this._snackBar.open('Error loading variance subcategory data.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
      }
    );
  }

  loadAuditData() {
    this.crudLoadingaudit = true;
    this.noRecords = false;

    const params = {
      action_code: 'euekku',
      tax_year: this.globalService?.tax_year,
      scenario: this.globalService?.scenario,
      jcd_key: this.globalService?.jcd_key,
      source_cc_key: this._rowData.SOURCE_COMBINATION_KEY
    };

    this.wwniMappingService.loadAuditData(
      this.resolveApiBase(),
      params,
      this.globalService,
      this._filterParams,
      this._rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.crudLoadingaudit = false;

        if (response.errorMessage && response.errorMessage !== 'null') {
          this._snackBar.open(response.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this.auditData = response.jsonObject || [];
          this.noRecords = this.auditData.length === 0;

          if (!this.noRecords) {
            // Initialize sorting
            this.column = this.viewColumnNames[0];
            this.reverse = false;
          }
        }
      },
      (error: any) => {
        this.crudLoadingaudit = false;
        this._snackBar.open('Error loading audit data.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
      }
    );
  }

  // Tab management
  onTabChange(tabId: number) {
    this.activeTab = tabId;
    if (tabId === 1) {
      this.trialBalanceCall();
    } else if (tabId === 2) {
      this.auditCall();
    }
  }

  trialBalanceCall() {
    // Main data is already loaded in init()
    this.currentTabName = "WWNI Allocation";
  }

  auditCall() {
    this.currentTabName = "Audit";
    this.loadAuditData();
  }

  // Entity management
  highlight(index: number) {
    for (let i = 0; i < this.entities.length; i++) {
      this.entities[i].colorvalue = (i === index);
    }

    if (this.entities[index]) {
      this.MAIN_SOURCE_AMT = this.entities[index].data?.TOTAL_SOURCE || 0;
      // Reload data for selected entity
      this.loadWwniData();
    }
  }

  removeEntity(index: number) {
    this.entities.splice(index, 1);

    if (this.entities.length === 0) {
      this.cancel();
      return;
    }

    // Reset colors and select first entity
    for (let i = 0; i < this.entities.length; i++) {
      this.entities[i].colorvalue = (i === 0);
    }

    if (this.entities.length > 0) {
      this.MAIN_SOURCE_AMT = this.entities[0].data?.TOTAL_SOURCE || 0;
      this.loadWwniData();
    }
  }

  // Mapping management
  addRepointVariance() {
    const newMapping = {
      ALLOCATED_AMT: 0,
      VARIANCE: 0,
      SYSTEM_AMT_USD: 0,
      tagetlistADD: true,
      colorvaluevariance: "gray",
      COLUMN_FLAG: 'F',
      ROW_INDICATOR: 'M',
      TARGET_COMBINATION_KEY: 0,
      TARGET_KEY: '',
      saveError: false,
      open: false, // Initialize expand/collapse state
      variancedata: [{
        VARIANCE_ALLOCATED: 0,
        VARIANCE_EXPL_CODE: '',
        VARIANCE_SUB_CATEFORY: '',
        VARIANCE_EXPL_CODE_KEY: '',
        ROW_KEY: '',
        VARIANCE_COMMENTS: '',
        variance_Dropdown_Flag: false,
        varianceError: false,
        subcateDropdown: []
      }]
    };

    this.mappingList.push(newMapping);
  }

  removeRepointAmountOne(index: number) {
    this.mappingList.splice(index, 1);
    this.calculateTotals();
    this.updateVarianceColors();
  }

  // Variance management
  varianceAdd(index: number) {
    if (this.mappingList[index]) {
      this.mappingList[index].variancedata.push({
        VARIANCE_ALLOCATED: 0,
        VARIANCE_EXPL_CODE: '',
        VARIANCE_SUB_CATEFORY: '',
        VARIANCE_SUB_CATEFORY_KEY: '',
        VARIANCE_EXPL_CODE_KEY: '',
        VARIANCE_COMMENTS: '',
        variance_Dropdown_Flag: false,
        varianceError: false,
        subcateDropdown: []
      });

      // Auto-open the variance section when adding new variance
      this.mappingList[index].open = true;
    }
  }

  varianceRemove(mappingIndex: number, varianceIndex: number) {
    if (this.mappingList[mappingIndex] && this.mappingList[mappingIndex].variancedata) {
      this.mappingList[mappingIndex].variancedata.splice(varianceIndex, 1);
      this.calculateVarianceForMapping(mappingIndex);

      // Auto-close if no meaningful variance data remains
      this.mappingList[mappingIndex].open = this.hasVarianceData(this.mappingList[mappingIndex].variancedata);
    }
  }

  // Helper method to check if variance data has meaningful content
  hasVarianceData(varianceData: any[]): boolean {
    if (!varianceData || varianceData.length === 0) {
      return false;
    }

    // Check if any variance item has meaningful data
    return varianceData.some(variance =>
      (variance.VARIANCE_ALLOCATED && variance.VARIANCE_ALLOCATED !== 0) ||
      (variance.VARIANCE_EXPL_CODE && variance.VARIANCE_EXPL_CODE !== '') ||
      (variance.VARIANCE_SUB_CATEFORY && variance.VARIANCE_SUB_CATEFORY !== '') ||
      (variance.VARIANCE_COMMENTS && variance.VARIANCE_COMMENTS !== '')
    );
  }

  // Calculations
  calculateTotals() {
    this.netIncPerLoss = 0;
    this.netIncRemain = 0;
    this.totalVarianceAllocateda = 0;

    for (let mapping of this.mappingList) {
      this.netIncPerLoss += Number(mapping.ALLOCATED_AMT || 0);
      this.netIncRemain += (mapping.SYSTEM_AMT_USD || 0);

      for (let variance of mapping.variancedata || []) {
        this.totalVarianceAllocateda += (variance.VARIANCE_ALLOCATED || 0);
      }
    }

    this.totalVariancea = this.netIncPerLoss - this.netIncRemain;
    this.totalVarianceRemaina = this.totalVariancea - this.totalVarianceAllocateda;

    this.changeColorwwni = (this.netIncRemain !== 0);
    this.changeColorVariance = (this.totalVarianceRemaina !== 0);
  }

  updateVarianceColors() {
    for (let mapping of this.mappingList) {
      let tempInternalVariance = 0;

      for (let variance of mapping.variancedata || []) {
        tempInternalVariance += (variance.VARIANCE_ALLOCATED || 0);
      }

      if (mapping.VARIANCE === tempInternalVariance) {
        mapping.colorvaluevariance = "green";
      } else if (mapping.VARIANCE !== 0) {
        mapping.colorvaluevariance = "red";
      } else {
        mapping.colorvaluevariance = "gray";
      }
    }
  }

  calculateVarianceForMapping(index: number) {
    if (this.mappingList[index]) {
      let tempInternalVariance = 0;

      for (let variance of this.mappingList[index].variancedata || []) {
        tempInternalVariance += (variance.VARIANCE_ALLOCATED || 0);
      }

      // Update color based on variance match
      if (this.mappingList[index].VARIANCE === tempInternalVariance) {
        this.mappingList[index].colorvaluevariance = "green";
      } else if (this.mappingList[index].VARIANCE !== 0) {
        this.mappingList[index].colorvaluevariance = "red";
      } else {
        this.mappingList[index].colorvaluevariance = "gray";
      }
    }

    this.calculateTotals();
  }

  netINC(index: number) {
    if (this.mappingList[index]) {
      this.mappingList[index].VARIANCE =
        (this.mappingList[index].ALLOCATED_AMT || 0) -
        (this.mappingList[index].SYSTEM_AMT_USD || 0);
    }

    this.calculateTotals();
    this.updateVarianceColors();
  }

  applyButton(index: number) {
    if (this.mappingList[index]) {
      this.mappingList[index].ALLOCATED_AMT += this.MAIN_SOURCE_AMT - this.netIncPerLoss;
      this.netINC(index);
    }
  }

  // Sorting for audit tab
  sortBy(colNum: number, sortKey: number) {
    this.column = this.viewColumnNames[colNum - 1];

    if (sortKey === 0 || sortKey === 2) {
      this.sort_key[colNum] = 1;
      this.reverse = false;
    } else if (sortKey === 1) {
      this.sort_key[colNum] = 2;
      this.reverse = true;
    }

    // Reset other sort keys
    for (let i = 1; i < 9; i++) {
      if (i !== colNum) {
        this.sort_key[i] = 0;
      }
    }
  }

  // Save functionality
  save() {
    this.isSaveClicked = true;
    this.crudLoading = true;

    // Validate required fields
    let hasErrors = false;
    for (let mapping of this.mappingList) {
      if (!mapping.TARGET_KEY) {
        mapping.saveError = true;
        hasErrors = true;
      } else {
        mapping.saveError = false;
      }

      for (let variance of mapping.variancedata || []) {
        if (variance.VARIANCE_ALLOCATED !== 0 && !variance.VARIANCE_EXPL_CODE) {
          variance.varianceError = true;
          hasErrors = true;
        } else {
          variance.varianceError = false;
        }
      }
    }

    if (hasErrors) {
      this.isSaveClicked = false;
      this.crudLoading = false;
      this._snackBar.open('Please correct the errors below', 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    // Build save data
    const saveData = this.buildSaveData();

    this.wwniMappingService.saveWwniMapping(
      this.resolveApiBase(),
      saveData,
      this.globalService,
      this._filterParams,
      this._rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.callSuccess !== "1") {
          this._snackBar.open(response.errorMessage || 'Error occurred while saving', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open('Data has been saved successfully', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        }
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
      }
    );
  }

  buildSaveData(): any[] {
    const saveData: any[] = [];

    // Build main mapping data
    for (let mapping of this.mappingList) {
      const mainData = {
        tax_year: this.globalService?.tax_year,
        scenario: this.globalService?.scenario,
        jcd_key: this.globalService?.jcd_key,
        source_combination_key: this._rowData.SOURCE_COMBINATION_KEY,
        target_combination_key: mapping.TARGET_COMBINATION_KEY,
        map_key: mapping.MAP_KEY,
        source_header_key: this._rowData.SOURCE_HEADER_KEY,
        target_header_key: mapping.TARGET_HEADER_KEY,
        target_row_flag: mapping.ROW_INDICATOR,
        target_chart_key: mapping.TARGET_CHART_KEY,
        target_acct_key_1: mapping.TARGET_ACCT_KEY,
        target_acct_key_2: mapping.VARIANCE_SUB_CATEFORY_KEY,
        target_amount: mapping.ALLOCATED_AMT,
        target_comments: mapping.COMMENTS,
        ROW_KEY: mapping.ROW_KEY,
        typeOf: "Edit"
      };
      saveData.push(mainData);

      // Build variance data
      for (let variance of mapping.variancedata || []) {
        if (variance.VARIANCE_ALLOCATED !== 0) {
          // Find variance account keys
          for (let subcat of this.varianceSubcategory) {
            if (subcat.VARIANCE_EXPL_CODE === variance.VARIANCE_EXPL_CODE) {
              variance.VARIANCE_ACCT_KEY = subcat.VARIANCE_ACCT_KEY;
              variance.VARIANCE_CHART_KEY = subcat.VARIANCE_CHART_KEY;
            }
            if (subcat.VARIANCE_SUB_CATEFORY === variance.VARIANCE_SUB_CATEFORY) {
              variance.VARIANCE_SUB_CATEFORY_KEY = subcat.VARIANCE_SUB_CATEFORY_KEY;
            }
          }

          const varianceData = {
            tax_year: this.globalService?.tax_year,
            scenario: this.globalService?.scenario,
            jcd_key: this.globalService?.jcd_key,
            source_combination_key: this._rowData.SOURCE_COMBINATION_KEY,
            target_combination_key: mapping.TARGET_COMBINATION_KEY,
            map_key: mapping.MAP_KEY,
            source_header_key: this._rowData.SOURCE_HEADER_KEY,
            target_header_key: mapping.TARGET_HEADER_KEY,
            target_row_flag: "V",
            target_acct_key_1: variance.VARIANCE_ACCT_KEY,
            target_acct_key_2: variance.VARIANCE_SUB_CATEFORY_KEY,
            target_chart_key: variance.VARIANCE_CHART_KEY,
            target_amount: variance.VARIANCE_ALLOCATED,
            target_comments: variance.VARIANCE_COMMENTS,
            ROW_KEY: variance.ROW_KEY,
            typeOf: "Edit"
          };
          saveData.push(varianceData);
        }
      }
    }

    return saveData;
  }

  reset() {
    this.loadWwniData();
  }

  cancel() {
    // Clear component data to ensure fresh state
    this.mappingList = [];
    this.displayNdata = [];
    this.displayYdata = [];
    this.sysLeidDropdownNdata = [];
    this.auditData = [];
    this._rowData = null;
    console.log('Angular component data cleared on cancel');
    this.requestGridUpdate.emit('cancel');
  }

  // Accordion management
  toggleEntityAccordion() {
    this.entityAccordionOpen = !this.entityAccordionOpen;
  }

  toggleSummaryAccordion() {
    this.summaryAccordionOpen = !this.summaryAccordionOpen;
  }

  // Show/hide details for variance rows
  showDetails(index: number, show: boolean) {
    if (this.mappingList[index]) {
      this.mappingList[index].open = show;
    }
  }

  // Target key selection
  targetKey(index: number, row: any) {
    for (let item of this.displayNdata) {
      if (item.TARGET_KEY === row.TARGET_KEY) {
        this.mappingList[index].ALLOCATED_AMT = item.ALLOCATED_AMT;
        this.mappingList[index].COLUMN_FLAG = item.COLUMN_FLAG;
        this.mappingList[index].DEFAULT_FLAG = item.DEFAULT_FLAG;
        this.mappingList[index].SYSTEM_AMT_LOCAL = item.SYSTEM_AMT_LOCAL;
        this.mappingList[index].EXCHANGE_RATE = item.EXCHANGE_RATE;
        this.mappingList[index].MAP_ACCT_KEY = item.MAP_ACCT_KEY;
        this.mappingList[index].MAP_KEY = item.MAP_KEY;
        this.mappingList[index].ROW_KEY = item.ROW_KEY;
        this.mappingList[index].SYSTEM_AMT_USD = item.SYSTEM_AMT_USD;
        this.mappingList[index].VARIANCE = item.VARIANCE;
        this.mappingList[index].COMMENTS = item.COMMENTS;
        this.mappingList[index].TARGET_HEADER_KEY = item.TARGET_HEADER_KEY;
        this.mappingList[index].ROW_INDICATOR = item.ROW_INDICATOR;
        this.mappingList[index].VARIANCE_SUB_CATEFORY_KEY = item.VARIANCE_SUB_CATEFORY_KEY;
        this.mappingList[index].TARGET_COMBINATION_KEY = item.TARGET_COMBINATION_KEY;
        this.mappingList[index].TARGET_CHART_KEY = item.TARGET_CHART_KEY;
        this.mappingList[index].TARGET_ACCT_KEY = item.TARGET_ACCT_KEY;
        break;
      }
    }

    this.calculateTotals();
    this.calculateVarianceForMapping(index);
  }

  // Variance explanation code selection
  varianceExplCode(mappingIndex: number, varianceIndex: number, variance: any) {
    const subcategories: string[] = [];

    for (let subcat of this.varianceSubcategory) {
      if (subcat.VARIANCE_EXPL_CODE === variance.VARIANCE_EXPL_CODE) {
        subcategories.push(subcat.VARIANCE_SUB_CATEFORY);
      }
    }

    const uniqueSubcategories = [...new Set(subcategories)];

    if (this.mappingList[mappingIndex] && this.mappingList[mappingIndex].variancedata[varianceIndex]) {
      if (uniqueSubcategories.length > 1) {
        this.mappingList[mappingIndex].variancedata[varianceIndex].variance_Dropdown_Flag = true;
        this.mappingList[mappingIndex].variancedata[varianceIndex].subcateDropdown = uniqueSubcategories;
      } else {
        this.mappingList[mappingIndex].variancedata[varianceIndex].variance_Dropdown_Flag = false;
        this.mappingList[mappingIndex].variancedata[varianceIndex].VARIANCE_SUB_CATEFORY = '';
        this.mappingList[mappingIndex].variancedata[varianceIndex].subcateDropdown = [];
      }
    }
  }
}
